import React, { JSX, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { TextType, DeviceType, applyTypography, media } from '../../styles/typography';

interface TypographyProps {
  children: ReactNode;
  type: TextType;
  color?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  as?: keyof JSX.IntrinsicElements;
  onClick?: () => void;
}

const StyledTypography = styled.div<Omit<TypographyProps, 'as' | 'children'>>`
  ${({ type }) => css`
    ${media.desktop} {
      ${applyTypography(type, DeviceType.DESKTOP)}
    }
    
    ${media.mobile} {
      ${applyTypography(type, DeviceType.MOBILE)}
    }
  `}
  
  color: ${({ color }) => color || 'inherit'}!important;
  text-align: ${({ align }) => align || 'left'};
`;

export const Typography: React.FC<TypographyProps> = ({
  children,
  type,
  color,
  align,
  className,
  as,
  onClick,
}) => {
  // Определяем HTML-элемент на основе типа текста
  const getDefaultElement = (): keyof JSX.IntrinsicElements => {
    switch (type) {
      case TextType.H1:
        return 'h1';
      case TextType.H2:
        return 'h2';
      case TextType.H3:
        return 'h3';
      case TextType.H4:
        return 'h4';
      case TextType.SUBTITLE1:
      case TextType.SUBTITLE2:
        return 'h5';
      default:
        return 'p';
    }
  };

  return (
    <StyledTypography
      as={as || getDefaultElement()}
      type={type}
      color={color}
      align={align}
      className={className}
      onClick={onClick}
    >
      {children}
    </StyledTypography>
  );
};

// Предопределенные компоненты для удобства использования
export const H1: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.H1} />
);

export const H2: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.H2} />
);

export const H3: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.H3} />
);

export const H4: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.H4} />
);

export const Subtitle1: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.SUBTITLE1} />
);

export const Subtitle2: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.SUBTITLE2} />
);

export const TextLarge: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.TEXT_LARGE} />
);

export const TextMedium: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.TEXT_MEDIUM} />
);

export const TextSmall: React.FC<Omit<TypographyProps, 'type'>> = (props) => (
  <Typography {...props} type={TextType.TEXT_SMALL} />
);

// Реэкспорт типов для удобства использования
export { TextType, DeviceType } from '../../styles/typography';
